import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { exact, func, number, string } from 'prop-types';
import { useSelector } from 'react-redux';

import { BootstrapTooltip, Dropdown, Icon, TimeField } from '@atoms';
import { DateSelectingModal } from '@molecules';
import { LocationList } from '@organisms';
import { getDateLabel } from '@helpers/time';
import { theme } from '@styles';
import { Button, Flex, Typography } from '@mixins';
import { SUCCESS } from '@constants/requestPhase';
import { dayjs } from '@utils';

import { ReactComponent as ExclamationMark } from '@assets/svgs/ExclamationMark.svg';

const GlobalAccessForm = ({ request, onSubmit, onRequestCancel }) => {
  const isEditing = useMemo(() => request && !!Object.keys(request).length, [request]);
  const requestFirstDate = useMemo(() => dayjs(request?.firstDate).tz('UTC').startOf('day'), [request?.firstDate]);
  const requestSecondDate = useMemo(() => dayjs(request?.secondDate).tz('UTC').startOf('day'), [request?.secondDate]);
  const requestTime = useMemo(
    () => dayjs(new Date()).set('hour', request?.time?.split(':')[0]).set('minute', request?.time?.split(':')[1]),
    [request?.time],
  );

  const [locationId, setLocationId] = useState(request?.location?.id);
  const [firstDate, setFirstDate] = useState(isEditing ? requestFirstDate : null);
  const [secondDate, setSecondDate] = useState(isEditing ? requestSecondDate : null);
  const [time, setTime] = useState(isEditing ? requestTime : null);

  const [isFirstDateModalOpen, setIsFirstDateModalOpen] = useState(false);
  const [isSecondDateModalOpen, setIsSecondDateModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  const { locations, currentLocation } = useSelector(store => store.locationStore);

  const locationsList = useMemo(
    () => locations?.flatMap(country => country.cities.flatMap(city => city.locations)),
    [locations.length],
  );
  const selectedLocation = useMemo(
    () => locationsList.find(location => location.id === locationId),
    [locationId, locationsList.length],
  );

  const isTimeSelected = useMemo(() => !!time?.hour() && (!!time?.minute() || time?.minute() === 0));
  const isTimeCorrect = useMemo(() => time?.hour() > 8 && time?.hour() < 17);
  const isTimeInThePast = useMemo(
    () => time && firstDate && dayjs().$D === firstDate.$D && dayjs().format('HH:mm') > time.format('HH:mm'),
    [firstDate, time],
  );

  const isFormInvalid = useMemo(
    () => !locationId || !firstDate || !secondDate || !isTimeSelected || !isTimeCorrect || isTimeInThePast,
    [locationId, firstDate, secondDate, time],
  );

  const isFormDirty = useMemo(
    () =>
      firstDate !== requestFirstDate ||
      secondDate !== requestSecondDate ||
      time !== requestTime ||
      locationId !== request?.location?.id,
    [request, firstDate, secondDate, time, locationId],
  );

  const { requestAccessPhase } = useSelector(store => store.globalAccessStore);

  const resetForm = useCallback(() => {
    setLocationId(null);
    setFirstDate(null);
    setSecondDate(null);
    setTime(null);
  }, []);

  useEffect(() => {
    if (requestAccessPhase === SUCCESS) {
      resetForm();
    }
  }, [requestAccessPhase]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      locationId,
      from: dayjs(firstDate).endOf('day').toISOString(),
      to: dayjs(secondDate).endOf('day').toISOString(),
      arriveAt: `${time.hour() > 9 ? time.hour() : `0${time.hour()}`}:${
        time.minute() > 9 ? time.minute() : `0${time.minute()}`
      }`,
    });
  }, [firstDate, locationId, onSubmit, secondDate, time]);

  const handleFirstDateModalChangeState = useCallback(() => {
    setIsFirstDateModalOpen(prevValue => {
      if (!prevValue) {
        setIsLocationModalOpen(false);
        setIsSecondDateModalOpen(false);
      }
      return !prevValue;
    });
  }, []);

  const handleSecondDateModalChangeState = useCallback(() => {
    setIsSecondDateModalOpen(prevValue => {
      if (!prevValue) {
        setIsLocationModalOpen(false);
        setIsFirstDateModalOpen(false);
      }
      return !prevValue;
    });
  }, []);

  const handleLocationModalChangeState = useCallback(() => {
    setIsLocationModalOpen(prevValue => {
      if (!prevValue) {
        setIsFirstDateModalOpen(false);
        setIsSecondDateModalOpen(false);
      }

      return !prevValue;
    });
  }, []);

  const handleFirstDateSelect = useCallback(
    selectedDate => {
      const offset = dayjs().$offset ?? 0;

      handleFirstDateModalChangeState();
      setFirstDate(
        dayjs(selectedDate)
          .add(-(offset + new Date(selectedDate).getTimezoneOffset()), 'minutes')
          .add(dayjs().$u ? -new Date(selectedDate).getTimezoneOffset() : 0, 'minute'),
      );
    },
    [dayjs().$offset, dayjs().$u, new Date().getTimezoneOffset()],
  );

  const handleSecondDateSelect = useCallback(
    selectedDate => {
      const offset = dayjs().$offset ?? 0;

      handleSecondDateModalChangeState();
      setSecondDate(
        dayjs(selectedDate)
          .add(-(offset + new Date(selectedDate).getTimezoneOffset()), 'minutes')
          .add(dayjs().$u ? -new Date(selectedDate).getTimezoneOffset() : 0, 'minute'),
      );
    },
    [dayjs().$offset, dayjs().$u, new Date().getTimezoneOffset()],
  );

  const handleTimeSelect = newTime => {
    setTime(newTime);
  };

  const handleLocationSelect = id => () => {
    handleLocationModalChangeState();
    setLocationId(id);
  };

  return (
    <Flex flexDirection="column">
      <Dropdown
        onDropdownClick={handleLocationModalChangeState}
        value={selectedLocation?.name}
        defaultValue="Location"
        title="Location to Visit*"
        position="relative"
        mb="40px"
        width={isEditing ? '100%' : '65%'}>
        {isLocationModalOpen && (
          <LocationList
            globalAccessAvailable
            selectedLocationId={locationId}
            topPosition={[60]}
            isOpen={isLocationModalOpen}
            onLocationSelect={handleLocationSelect}
            onOpenStatusChange={handleLocationModalChangeState}
          />
        )}
      </Dropdown>
      <Flex
        position="relative"
        mb={50}
        width="100%"
        flexDirection={isEditing ? 'column' : 'row'}
        justifyContent="space-between">
        <Flex
          marginBottom={isEditing ? '40px' : '0px'}
          justifyContent="space-between"
          width={isEditing ? '100%' : '65%'}>
          <Dropdown
            onDropdownClick={handleFirstDateModalChangeState}
            value={firstDate ? getDateLabel(firstDate) : null}
            defaultValue="First day"
            alignSelf="end"
            title="Dates of Visit*"
            position="relative"
            width="43%">
            {(isFirstDateModalOpen || isSecondDateModalOpen) && (
              <DateSelectingModal
                {...(isFirstDateModalOpen && secondDate && { maxDate: secondDate.$d })}
                {...(isSecondDateModalOpen && firstDate && { minDate: firstDate.$d })}
                canBeClosed={false}
                hasSelectingButton={false}
                onSelect={isFirstDateModalOpen ? handleFirstDateSelect : handleSecondDateSelect}
                onModalClose={isFirstDateModalOpen ? handleFirstDateModalChangeState : handleSecondDateModalChangeState}
                calendarDate={isFirstDateModalOpen ? (firstDate?.$d ?? null) : (secondDate?.$d ?? null)}
                type="month"
                topPosition={82}
              />
            )}
          </Dropdown>
          <Dropdown
            onDropdownClick={handleSecondDateModalChangeState}
            alignSelf="end"
            position="relative"
            value={secondDate ? getDateLabel(secondDate) : null}
            defaultValue="Last day"
            width="43%"
          />
        </Flex>
        <Flex flexDirection="column" width={isEditing ? '100%' : '30%'} alignSelf="end">
          <Flex>
            <BootstrapTooltip title="Open Hours: 9:00 - 17:00" placement="top">
              <Flex mb={10} alignItems="center">
                <Typography mr="8px" variant="rightserif-400" fontSize={[12, 14, 16]}>
                  Time of arrival
                </Typography>
                <Icon height={16} minWidth={16} mr="8px" SVG={ExclamationMark} />
              </Flex>
            </BootstrapTooltip>
          </Flex>
          <Flex
            width="100%"
            paddingBottom="6px"
            borderBottom={`1px solid ${
              isTimeSelected && (!isTimeCorrect || isTimeInThePast) ? theme.color.red : theme.color.gray[500]
            }`}>
            <TimeField
              ampm={currentLocation.country === 'US'}
              value={time}
              isValid={(isTimeCorrect && !isTimeInThePast) || !isTimeSelected}
              onChange={handleTimeSelect}
              minTime={dayjs(new Date()).set('hour', 9).set('minute', 0)}
              maxTime={dayjs(new Date()).set('hour', 16).set('minute', 59)}
            />
          </Flex>
        </Flex>
        {isTimeSelected && (!isTimeCorrect || isTimeInThePast) && (
          <Typography position="absolute" bottom={-30} variant="diatype-400" fontSize={12} color={theme.color.red}>
            {!isTimeCorrect
              ? 'Please select a time between 9:00 and 17:00.'
              : 'Please choose time of arrival in the future'}
          </Typography>
        )}
      </Flex>
      <Button
        disabled={isFormInvalid || !isFormDirty}
        alignSelf={isEditing ? 'center' : 'start'}
        onClick={handleSubmit}
        variant="primary"
        py={15}
        px={15}>
        {isEditing ? 'Update Access Request' : 'Request Access'}
      </Button>
      {isEditing && onRequestCancel && (
        <Button mt="30px" color={theme.color.mintMinded[900]} onClick={onRequestCancel(request.id)}>
          Cancel
        </Button>
      )}
    </Flex>
  );
};

GlobalAccessForm.propTypes = {
  onRequestCancel: func,
  onSubmit: func.isRequired,
  request: exact({
    id: number,
    firstDate: string,
    secondDate: string,
    time: string,
    location: exact({
      id: number,
      image: string,
      title: string,
    }),
  }),
};

GlobalAccessForm.defaultProps = {
  request: {},
  onRequestCancel: () => {},
};

export default GlobalAccessForm;
